// Carts.js - Server API Version

import React, { useState, useEffect, useRef } from 'react';
import { FaShoppingCart, FaSearch, FaPlus, FaTrash, FaEdit, FaCheckDouble, FaEllipsisV, FaEnvelope, FaChevronDown } from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';
import { useCurrency } from '../../context/CurrencyContext';
import './Carts.css';

// ============================================================
// ✅ SERVER API URL - YEH CHANGE KIYA HAI
// ============================================================
const API_BASE_URL = 'https://super-backend.hira-tech.co.uk';

const Carts = () => {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const { formatPrice, currency, loading: currencyLoading } = useCurrency();

  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCart, setEditingCart] = useState(null);
  const [selectedCarts, setSelectedCarts] = useState([]);
  const [showActionDropdown, setShowActionDropdown] = useState(false);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [formData, setFormData] = useState({
    customerId: '',
    deliveryCharges: '',
    totalAmount: '',
    city: ''
  });
  const [saveLoading, setSaveLoading] = useState(false);
  const [shops, setShops] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [selectedShopId, setSelectedShopId] = useState('');
  const [currentShopName, setCurrentShopName] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [ukShopId, setUkShopId] = useState(null);
  const [isUkShopSet, setIsUkShopSet] = useState(false);
  const [cities, setCities] = useState([]);

  const customerDropdownRef = useRef(null);
  const inputRef = useRef(null);
  const modalContentRef = useRef(null);

  const [carts, setCarts] = useState([]);

  const isAllShopsMode = shopId === 'all';
  const isNoShopId = !shopId;

  const extractNumericValue = (value) => {
    if (!value) return 0;
    if (typeof value === 'number') return value;
    const numericString = String(value).replace(/[^0-9.-]/g, '');
    const parsed = parseFloat(numericString);
    return isNaN(parsed) ? 0 : parsed;
  };

  const showSuccessAlert = (message) => {
    Swal.fire({
      icon: 'success',
      title: 'Success!',
      text: message,
      timer: 2000,
      showConfirmButton: true,
      confirmButtonColor: '#004aad'
    });
  };

  const showErrorAlert = (message) => {
    Swal.fire({
      icon: 'error',
      title: 'Error!',
      text: message,
      confirmButtonColor: '#d33'
    });
  };

  const showWarningAlert = (message) => {
    Swal.fire({
      icon: 'warning',
      title: 'Warning!',
      text: message,
      confirmButtonColor: '#f0ad4e'
    });
  };

  const showDeleteConfirmation = (cartId, isMultiple = false, count = 1) => {
    const title = isMultiple
      ? `Are you sure you want to delete ${count} cart(s)?`
      : `Are you sure you want to delete cart "${cartId}"?`;

    return Swal.fire({
      title: 'Are you sure?',
      text: title,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });
  };

  const findUkShop = (shopsList) => {
    const ukShop = shopsList.find(shop =>
      shop.shop_name?.toLowerCase().includes('uk')
    );
    return ukShop || shopsList[0];
  };

  const getAuthToken = () => {
    const possibleKeys = ['token', 'authToken', 'access_token', 'accessToken', 'jwt'];

    for (let key of possibleKeys) {
      const storedData = localStorage.getItem(key);
      if (storedData) {
        try {
          const parsed = JSON.parse(storedData);
          if (parsed.access || parsed.token || parsed.access_token) {
            return parsed.access || parsed.token || parsed.access_token;
          }
          if (parsed.token && typeof parsed.token === 'object') {
            return parsed.token.access || parsed.token.token || parsed.token.access_token;
          }
        } catch (e) {
          return storedData;
        }
      }
    }
    return null;
  };

  const fetchShops = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/product/shops/`);
      if (response.data) {
        const shopsArray = Array.isArray(response.data) ? response.data : [];
        setShops(shopsArray);

        const ukShop = findUkShop(shopsArray);
        if (ukShop) {
          setUkShopId(ukShop.shop_id);

          if (!shopId || shopId === 'all') {
            navigate(`/shop/${ukShop.shop_id}/carts`, { replace: true });
            setIsUkShopSet(true);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching shops:', error);
    }
  };

  const fetchCitiesForShop = async (shopId) => {
    if (!shopId) {
      setCities([]);
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/api/product/shop-cities/?shop_id=${shopId}`);

      if (response.data) {
        const citiesList = response.data.cities || [];
        setCities(citiesList);
      } else {
        setCities([]);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
      setCities([]);
    }
  };

  // ============================================================
  // ✅ FIXED: fetchAllCustomers - now uses API_BASE_URL
  // ============================================================
  const fetchAllCustomers = async () => {
    try {
      const token = getAuthToken();

      if (!token) {
        console.warn('No token found for fetching customers');
        return;
      }

      // ✅ FIXED: Use API_BASE_URL instead of hardcoded localhost
      const apiUrl = `${API_BASE_URL}/user/getAllcustomers/`;

      console.log('Fetching customers from:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Total customers received:', data.length);

        const formattedCustomers = data.map(customer => ({
          id: customer.id,
          email: customer.email || '',
          shopId: customer.shop || null,
        }));

        setCustomers(formattedCustomers);
        setFilteredCustomers(formattedCustomers);
      } else {
        console.error('Failed to fetch customers:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchCarts = async () => {
    try {
      let url = `${API_BASE_URL}/api/product/carts/`;

      let targetShopId = null;
      if (shopId && shopId !== 'all') {
        targetShopId = shopId;
      } else if (ukShopId) {
        targetShopId = ukShopId;
      }

      if (targetShopId) {
        url += `?shop_id=${targetShopId}`;
      }

      const response = await axios.get(url);

      if (response.data) {
        const processedCarts = response.data.map(cart => ({
          ...cart,
          deliveryChargesRaw: extractNumericValue(cart.delivery_Charges || cart.deliveryCharges || 0),
          totalAmountRaw: extractNumericValue(cart.total_Amount || cart.totalAmount || 0),
          city: cart.city || ''
        }));

        setCarts(processedCarts);

        if (shopId && shopId !== 'all') {
          if (response.data.length > 0 && response.data[0].shop) {
            setCurrentShopName(response.data[0].shop.shop_name);
          } else {
            try {
              const shopResponse = await axios.get(`${API_BASE_URL}/api/product/shops/${shopId}/`);
              if (shopResponse.data) {
                setCurrentShopName(shopResponse.data.shop_name);
              }
            } catch (err) {
              console.error('Error fetching shop details:', err);
              setCurrentShopName(`Shop ${shopId}`);
            }
          }
        } else if (ukShopId) {
          const ukShop = shops.find(s => s.shop_id === ukShopId);
          if (ukShop) {
            setCurrentShopName(ukShop.shop_name);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching carts:', err);
      if (err.response?.status === 404) {
        showErrorAlert('Carts API not available. Please check backend configuration.');
      } else {
        showErrorAlert('Failed to load carts data');
      }
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      await fetchShops();
      await fetchAllCustomers();
      await fetchCarts();
    };

    loadInitialData();
  }, [shopId, ukShopId]);

  useEffect(() => {
    if (isModalOpen) {
      let shopIdToUse = selectedShopId;

      if (!shopIdToUse && shopId && shopId !== 'all') {
        shopIdToUse = shopId;
      } else if (!shopIdToUse && ukShopId) {
        shopIdToUse = ukShopId;
      }

      if (shopIdToUse && shopIdToUse !== 'all' && shopIdToUse !== '') {
        const filtered = customers.filter(customer =>
          customer.shopId?.toString() === shopIdToUse.toString()
        );

        setFilteredCustomers(filtered);
      } else {
        setFilteredCustomers(customers);
      }
    }
  }, [selectedShopId, customers, isModalOpen, shopId, ukShopId]);

  useEffect(() => {
    if (isModalOpen) {
      let shopIdToUse = selectedShopId;

      if (!shopIdToUse && shopId && shopId !== 'all') {
        shopIdToUse = shopId;
      } else if (!shopIdToUse && ukShopId) {
        shopIdToUse = ukShopId;
      }

      if (shopIdToUse && shopIdToUse !== 'all' && shopIdToUse !== '') {
        fetchCitiesForShop(shopIdToUse);
      } else {
        setCities([]);
      }
    }
  }, [selectedShopId, isModalOpen, shopId, ukShopId]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredCarts = carts.filter(cart =>
    (cart.cartId?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (cart.customerId?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (cart.customer_Id?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  const handleSelectAll = () => {
    if (selectedCarts.length === filteredCarts.length) {
      setSelectedCarts([]);
    } else {
      setSelectedCarts(filteredCarts.map(cart => cart.cart_Id || cart.id));
    }
    setShowActionDropdown(false);
  };

  const handleSelectCart = (cartId) => {
    if (selectedCarts.includes(cartId)) {
      setSelectedCarts(selectedCarts.filter(id => id !== cartId));
    } else {
      setSelectedCarts([...selectedCarts, cartId]);
    }
    setShowActionDropdown(false);
  };

  const handleDeleteSelected = async () => {
    if (selectedCarts.length === 0) {
      showWarningAlert('Please select carts to delete');
      return;
    }

    const result = await showDeleteConfirmation(null, true, selectedCarts.length);

    if (result.isConfirmed) {
      setSaveLoading(true);
      try {
        let deletedCount = 0;
        for (const cartId of selectedCarts) {
          const response = await axios.delete(`${API_BASE_URL}/api/product/carts/${cartId}/delete/`);
          if (response.status === 200 || response.status === 204) {
            deletedCount++;
          }
        }

        showSuccessAlert(`${deletedCount} cart(s) deleted successfully!`);
        setSelectedCarts([]);
        setShowActionDropdown(false);
        await fetchCarts();
      } catch (error) {
        console.error('Error deleting carts:', error);
        showErrorAlert('Failed to delete carts. Please try again.');
      } finally {
        setSaveLoading(false);
      }
    }
  };

  const handleClearAll = () => {
    setSelectedCarts([]);
    setShowActionDropdown(false);
  };

  const handleCustomerSelect = (customer) => {
    setFormData({
      ...formData,
      customerId: customer.email || customer.id.toString()
    });
    setShowCustomerDropdown(false);
    setCustomerSearch('');
  };

  const handleCityChange = (e) => {
    setFormData({
      ...formData,
      city: e.target.value
    });
  };

  const handleOpenModalForNew = () => {
    setEditingCart(null);
    setFormData({
      customerId: '',
      deliveryCharges: '',
      totalAmount: '',
      city: ''
    });

    if (shopId && shopId !== 'all') {
      setSelectedShopId(shopId);
      fetchCitiesForShop(shopId);
    } else {
      setSelectedShopId('');
      setCities([]);
    }

    setCustomerSearch('');
    setShowCustomerDropdown(false);
    setIsModalOpen(true);
  };

  const handleOpenModalForEdit = (cart) => {
    setEditingCart(cart);
    setFormData({
      customerId: cart.customer_Id || cart.customerId || cart.cartId || '',
      deliveryCharges: cart.deliveryChargesRaw?.toString() || cart.delivery_Charges?.toString() || cart.deliveryCharges?.toString() || '',
      totalAmount: cart.totalAmountRaw?.toString() || cart.total_Amount?.toString() || cart.totalAmount?.toString() || '',
      city: cart.city || ''
    });
    setSelectedShopId(cart.shop?.shop_id?.toString() || cart.shop_id?.toString() || '');
    setCustomerSearch('');
    setShowCustomerDropdown(false);

    if (cart.shop?.shop_id) {
      fetchCitiesForShop(cart.shop.shop_id);
    } else if (cart.shop_id) {
      fetchCitiesForShop(cart.shop_id);
    }

    setIsModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleCustomerIdClick = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 5,
        left: rect.left + window.scrollX
      });
    }
    setShowCustomerDropdown(!showCustomerDropdown);
    setCustomerSearch('');
  };

  const handleShopChange = (e) => {
    const newShopId = e.target.value;
    setSelectedShopId(newShopId);

    setFormData({
      ...formData,
      customerId: '',
      city: ''
    });
    setCustomerSearch('');

    if (newShopId && newShopId !== 'all' && newShopId !== '') {
      const filtered = customers.filter(customer =>
        customer.shopId?.toString() === newShopId.toString()
      );
      setFilteredCustomers(filtered);

      fetchCitiesForShop(newShopId);
    } else {
      setFilteredCustomers(customers);
      setCities([]);
    }

    setShowCustomerDropdown(true);
  };

  const getFilteredCustomersForDropdown = () => {
    if (!customerSearch.trim()) {
      return filteredCustomers;
    }
    return filteredCustomers.filter(customer => {
      const email = (customer.email || '').toLowerCase();
      const search = customerSearch.toLowerCase();
      return email.includes(search);
    });
  };

  const handleSave = async () => {
    if (!formData.customerId.trim()) {
      showWarningAlert('Please enter Customer ID');
      return;
    }
    if (!formData.deliveryCharges) {
      showWarningAlert('Please enter Delivery Charges');
      return;
    }
    if (!formData.totalAmount) {
      showWarningAlert('Please enter Total Amount');
      return;
    }

    let targetShopId;
    if (editingCart) {
      targetShopId = editingCart.shop?.shop_id || editingCart.shop_id;
    } else if (shopId && shopId !== 'all') {
      targetShopId = parseInt(shopId);
    } else if (selectedShopId) {
      targetShopId = parseInt(selectedShopId);
    } else {
      targetShopId = ukShopId;
    }

    if (!targetShopId) {
      showWarningAlert('Please select a shop');
      return;
    }

    setSaveLoading(true);

    try {
      const cartData = {
        shop_id: targetShopId,
        customer_Id: formData.customerId.trim(),
        delivery_Charges: parseFloat(formData.deliveryCharges),
        total_Amount: parseFloat(formData.totalAmount),
        city: formData.city || null
      };

      if (editingCart !== null) {
        const cartId = editingCart.cart_Id || editingCart.id;
        const response = await axios.put(
          `${API_BASE_URL}/api/product/carts/${cartId}/update/`,
          cartData
        );

        if (response.status === 200) {
          showSuccessAlert('Cart updated successfully!');
          await fetchCarts();
          handleCloseModal();
        } else {
          showErrorAlert('Failed to update cart');
        }
      } else {
        const response = await axios.post(
          `${API_BASE_URL}/api/product/carts/create/`,
          cartData
        );

        if (response.status === 201) {
          showSuccessAlert('Cart added successfully!');
          await fetchCarts();
          handleCloseModal();
        } else {
          showErrorAlert('Failed to add cart');
        }
      }
    } catch (error) {
      console.error('Error saving cart:', error);

      if (error.response) {
        if (error.response.status === 400) {
          showErrorAlert('Invalid data provided. Please check your input.');
        } else if (error.response.status === 500) {
          showErrorAlert('Server error. Please check that the user exists in the system.');
        } else {
          showErrorAlert(error.response.data?.error || 'Failed to save cart. Please try again.');
        }
      } else if (error.request) {
        showErrorAlert('No response from server. Please check your connection.');
      } else {
        showErrorAlert('An error occurred. Please try again.');
      }
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async () => {
    if (editingCart !== null) {
      const result = await showDeleteConfirmation(formData.customerId || editingCart.cartId);

      if (result.isConfirmed) {
        setSaveLoading(true);
        try {
          const cartId = editingCart.cart_Id || editingCart.id;
          const response = await axios.delete(
            `${API_BASE_URL}/api/product/carts/${cartId}/delete/`
          );

          if (response.status === 200 || response.status === 204) {
            showSuccessAlert('Cart deleted successfully!');
            setSelectedCarts(selectedCarts.filter(id => id !== cartId));
            await fetchCarts();
            handleCloseModal();
          } else {
            showErrorAlert('Failed to delete cart');
          }
        } catch (error) {
          console.error('Error deleting cart:', error);
          showErrorAlert('Failed to delete cart. Please try again.');
        } finally {
          setSaveLoading(false);
        }
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCart(null);
    setFormData({
      customerId: '',
      deliveryCharges: '',
      totalAmount: '',
      city: ''
    });
    setSelectedShopId('');
    setCustomerSearch('');
    setCities([]);
    setShowCustomerDropdown(false);
  };

  const handleClickOutside = (e) => {
    if (showActionDropdown && !e.target.closest('.action-dropdown-container')) {
      setShowActionDropdown(false);
    }
    if (showCustomerDropdown && customerDropdownRef.current && !customerDropdownRef.current.contains(e.target) && e.target !== inputRef.current) {
      setShowCustomerDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showActionDropdown, showCustomerDropdown]);

  const isOperationInProgress = saveLoading || currencyLoading;

  const getShopsWithCustomers = () => {
    const shopIdsWithCustomers = new Set();
    customers.forEach(customer => {
      if (customer.shopId) {
        shopIdsWithCustomers.add(customer.shopId.toString());
      }
    });
    return shops.filter(shop =>
      shopIdsWithCustomers.has(shop.shop_id.toString())
    );
  };

  const shopsWithCustomers = getShopsWithCustomers();
  const displayCustomers = getFilteredCustomersForDropdown();

  return (
    <div className="carts-main-container">
      <div className="carts-header-section">
        <div className="header-content">
          <div>
            <h2>Shopping Carts</h2>
          </div>
        </div>
      </div>

      <div className="carts-content">
        <div className="carts-action-section">
          <div className="search-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by Cart ID or Customer ID..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
              disabled={isOperationInProgress}
            />
          </div>
          <div className="action-buttons">
            <button className="select-all-btn" onClick={handleSelectAll} disabled={isOperationInProgress}>
              <FaCheckDouble />
              <span>{selectedCarts.length === filteredCarts.length ? 'Deselect All' : 'Select All'}</span>
            </button>
            <button className="add-cart-btn" onClick={handleOpenModalForNew} disabled={isOperationInProgress}>
              <FaPlus />
              <span>ADD CART</span>
            </button>
          </div>
        </div>

        {selectedCarts.length > 0 && (
          <div className="selected-summary">
            <div className="summary-content">
              <span className="summary-icon">✓</span>
              <span className="summary-text">
                {selectedCarts.length} cart(s) selected
              </span>
              <div className="action-dropdown-container">
                <button
                  className="action-dropdown-btn"
                  onClick={() => setShowActionDropdown(!showActionDropdown)}
                  disabled={isOperationInProgress}
                >
                  <FaEllipsisV />
                  <span>Actions</span>
                </button>
                {showActionDropdown && (
                  <div className="action-dropdown-menu">
                    <button
                      className="dropdown-item delete-item"
                      onClick={handleDeleteSelected}
                      disabled={isOperationInProgress}
                    >
                      <FaTrash />
                      <span>Delete Selected ({selectedCarts.length})</span>
                    </button>
                  </div>
                )}
              </div>
              <button className="clear-selection" onClick={handleClearAll} disabled={isOperationInProgress}>
                Clear all
              </button>
            </div>
          </div>
        )}

        <div className="carts-table-container">
          <div className="carts-table-header">
            <h3>Shopping Carts List</h3>
            <span className="carts-count">{filteredCarts.length} carts</span>
          </div>

          <div className="carts-table-wrapper">
            <table className="carts-table">
              <thead>
                <tr>
                  <th style={{ width: '50px' }}>
                    <input
                      type="checkbox"
                      checked={selectedCarts.length === filteredCarts.length && filteredCarts.length > 0}
                      onChange={handleSelectAll}
                      disabled={isOperationInProgress}
                    />
                  </th>
                  <th>Cart ID / Customer</th>
                  <th>City</th>
                  <th>Delivery Charges ({currency})</th>
                  <th>Total Amount ({currency})</th>
                  <th style={{ width: '100px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCarts.length > 0 ? (
                  filteredCarts.map((cart) => {
                    const cartId = cart.cart_Id || cart.id;
                    return (
                      <tr key={cartId} className={selectedCarts.includes(cartId) ? 'selected' : ''}>
                        <td className="checkbox-cell">
                          <input
                            type="checkbox"
                            checked={selectedCarts.includes(cartId)}
                            onChange={() => handleSelectCart(cartId)}
                            disabled={isOperationInProgress}
                          />
                        </td>
                        <td className="cart-id-cell">
                          <span className="cart-id">{cart.cartId || cart.customer_Id || cart.customerId || cartId}</span>
                        </td>
                        <td>
                          {cart.city ? (
                            <span className="city-badge">{cart.city}</span>
                          ) : (
                            <span className="no-city">-</span>
                          )}
                        </td>
                        <td>{formatPrice(cart.deliveryChargesRaw || cart.delivery_Charges || cart.deliveryCharges || 0)}</td>
                        <td>{formatPrice(cart.totalAmountRaw || cart.total_Amount || cart.totalAmount || 0)}</td>
                        <td className="actions-cell">
                          <button
                            className="edit-cart-btn"
                            onClick={() => handleOpenModalForEdit(cart)}
                            title="Edit Cart"
                            disabled={isOperationInProgress}
                          >
                            <FaEdit />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" ref={modalContentRef} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingCart !== null ? 'Edit Shopping Cart' : 'Add Shopping Cart'}</h3>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <div className="modal-body">
              <form className="cart-form" onSubmit={(e) => e.preventDefault()}>
                {(!(isAllShopsMode || isNoShopId) || editingCart) && (
                  <div className="form-group">
                    <div className="shop-info-display">
                      <span className="shop-display-name">
                        {editingCart
                          ? (editingCart.shop?.shop_name || editingCart.shop_name || `Shop ${editingCart.shop_id}`)
                          : currentShopName}
                      </span>
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label-with-icon">
                    CUSTOMER ID *:
                  </label>
                  <div className="input-with-dropdown">
                    <input
                      ref={inputRef}
                      type="text"
                      name="customerId"
                      value={formData.customerId}
                      onChange={handleInputChange}
                      onClick={handleCustomerIdClick}
                      placeholder="Select or search customer email..."
                      autoFocus
                      className="cart-input"
                      disabled={isOperationInProgress}
                    />
                    <button
                      type="button"
                      className="dropdown-toggle-btn"
                      onClick={handleCustomerIdClick}
                      disabled={isOperationInProgress}
                    >
                      <FaChevronDown className={`dropdown-arrow ${showCustomerDropdown ? 'rotated' : ''}`} />
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label-with-icon">
                    CITY:
                  </label>
                  <div className="simple-select-wrapper">
                    <select
                      name="city"
                      value={formData.city}
                      onChange={handleCityChange}
                      className="city-select-input"
                      disabled={isOperationInProgress || cities.length === 0}
                    >
                      <option value="">Select City</option>
                      {cities.map((city, index) => (
                        <option key={index} value={city}>
                          {city}
                        </option>
                      ))}
                    </select>
                  </div>
                  {cities.length === 0 && (
                    <small className="field-hint" style={{ color: '#999' }}>
                      No cities available for this shop. Please add cities in shop settings.
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label-with-icon">
                    DELIVERY CHARGES ({currency}) *:
                  </label>
                  <input
                    type="number"
                    name="deliveryCharges"
                    value={formData.deliveryCharges}
                    onChange={handleInputChange}
                    placeholder={`Enter delivery charges in ${currency}`}
                    step="0.01"
                    min="0"
                    className="cart-input"
                    disabled={isOperationInProgress}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label-with-icon">
                    TOTAL AMOUNT ({currency}) *:
                  </label>
                  <input
                    type="number"
                    name="totalAmount"
                    value={formData.totalAmount}
                    onChange={handleInputChange}
                    placeholder={`Enter total amount in ${currency}`}
                    step="0.01"
                    min="0"
                    className="cart-input"
                    disabled={isOperationInProgress}
                  />
                </div>

                {formData.deliveryCharges && formData.totalAmount && (
                  <div className="summary-preview">
                    <h4>Order Summary</h4>
                    <div className="preview-row">
                      <span>Subtotal:</span>
                      <span>{formatPrice((parseFloat(formData.totalAmount) - parseFloat(formData.deliveryCharges || 0)))}</span>
                    </div>
                    <div className="preview-row">
                      <span>Delivery:</span>
                      <span>{formatPrice(parseFloat(formData.deliveryCharges || 0))}</span>
                    </div>
                    <div className="preview-row total">
                      <span>Total:</span>
                      <span>{formatPrice(parseFloat(formData.totalAmount || 0))}</span>
                    </div>
                  </div>
                )}
              </form>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={handleCloseModal} disabled={isOperationInProgress}>
                Cancel
              </button>
              {editingCart !== null && (
                <button className="btn-delete" onClick={handleDelete} disabled={isOperationInProgress}>
                  Delete
                </button>
              )}
              <button className="btn-save" onClick={handleSave} disabled={isOperationInProgress}>
                {isOperationInProgress ? 'Saving...' : (editingCart !== null ? 'Update Cart' : 'Save Cart')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCustomerDropdown && (
        <div
          className="customer-dropdown-outside"
          ref={customerDropdownRef}
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            minWidth: '300px',
            maxWidth: '400px',
            zIndex: 10001,
            display: 'block'
          }}
        >
          <div className="customer-dropdown-header">
            <FaEnvelope />
            <span>Select Customer Email</span>
          </div>
          <div className="customer-dropdown-list">
            {displayCustomers.length > 0 ? (
              displayCustomers.map((customer) => (
                <button
                  key={customer.id}
                  type="button"
                  className="customer-dropdown-item"
                  onClick={() => handleCustomerSelect(customer)}
                  disabled={isOperationInProgress}
                >
                  <FaEnvelope className="customer-icon" />
                  <span className="customer-email">{customer.email}</span>
                </button>
              ))
            ) : (
              <div className="customer-dropdown-no-data">
                {customerSearch ? 'No customers found matching your search' : 'No customers available for this shop'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Carts;